import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import AddIcon from "@mui/icons-material/Add";
import { Tooltip, MenuItem, Select, FormControl, InputLabel } from "@mui/material";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";

// RasPm Dashboard examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import dataTableData from "./data/dataTableData";
import { RequestService } from "services/RequestService";

// Form Component
import DepartmentForm from "../add";

function DepartmentsDataTable() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState(dataTableData);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "لوحة ادارة ربان السفينة",
    content: "",
    dateTime: new Date().toLocaleString('ar-IQ'),
    open: false,
  });

  const [updateDialog, setUpdateDialog] = useState({
    open: false,
    departmentData: null
  });
  const [addDialog, setAddDialog] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [search, setSearch] = useState("");
  const [firstSearch, setFirstSearch] = useState(false);

  // إضافة حالة Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalItems: 0
  });

  const [departmentForm, setDepartmentForm] = useState({
    name: "",
    description: "",
    companyId: ""
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const showNotification = (message, type = "success", backendResponse = null) => {
    setNotification({
      color: type,
      icon: type === "success" ? "check" : "error",
      title: "لوحة ادارة ربان السفينة",
      content: backendResponse?.data?.message || message,
      dateTime: new Date().toLocaleString('ar-IQ'),
      open: true
    });
  };

  // دوال معالجة Pagination
  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handlePageSizeChange = (newSize) => {
    setPagination({ page: 1, pageSize: newSize, totalItems: pagination.totalItems });
  };

  const handleUpdateClick = (departmentData) => {
    setDepartmentForm({
      name: departmentData.name || "",
      description: departmentData.description || "",
      companyId: departmentData.companyId || ""
    });
    setUpdateDialog({ open: true, departmentData });
  };

  const handleCloseUpdateDialog = () => {
    setUpdateDialog({ open: false, departmentData: null });
    setDepartmentForm({
      name: "",
      companyId: ""
    });
  };

  const handleUpdateSubmit = async () => {
    try {
      setLoading(true);
      const response = await RequestService.put(`Departments/UpdateDepartment/${updateDialog.departmentData.id}`, departmentForm);

      if (response.data && !response.data.error) {
        showNotification("تم تحديث بيانات القسم بنجاح", "success", response);
        handleCloseUpdateDialog();
        fetchDepartments();
      } else {
        showNotification("حدث خطأ أثناء تحديث بيانات القسم", "error", response);
      }
    } catch (error) {
      console.error("Error updating department:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء تحديث بيانات القسم", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setDepartmentForm({
      name: "",
      description: "",
      companyId: ""
    });
    setAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialog(false);
    setDepartmentForm({
      name: "",
      description: "",
      companyId: ""
    });
  };

  const handleAddSubmit = async () => {
    if (!departmentForm.name) {
      showNotification("الرجاء إدخال اسم القسم", "error");
    } else if (!departmentForm.companyId) {
      showNotification("الرجاء إختيار الشركة", "error");
    } else {
      try {
        setLoading(true);
        const response = await RequestService.post(`Departments/CreateDepartment`, departmentForm);

        if (response.data && !response.data.error) {
          showNotification("تم إضافة القسم بنجاح", "success", response);
          handleCloseAddDialog();
          fetchDepartments();
        } else {
          showNotification("حدث خطأ أثناء إضافة القسم", "error", response);
        }
      } catch (error) {
        console.error("Error adding department:", error);
        showNotification(error.response?.data?.message || "حدث خطأ أثناء إضافة القسم", "error", error.response);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteDepartment = async (id) => {
    try {
      setLoading(true);
      const response = await RequestService.delete(`Departments/${id}`);

      if (response.data && !response.data.error) {
        showNotification("تم حذف القسم بنجاح", "success", response);
        fetchDepartments();
      } else {
        showNotification("حدث خطأ أثناء حذف القسم", "error", response);
      }
    } catch (error) {
      console.error("Error deleting department:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء حذف القسم", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const transformDepartmentData = (departments) => {
    return departments.map((department) => ({
      id: department.id,
      name: department.name || "—",
      companyName: department.companyName || "—",
      description: department.description || "—",
      actions: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => handleUpdateClick(department)}
            startIcon={<Icon>edit</Icon>}
          >
            تعديل
          </MDButton>
          <MDButton
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDeleteDepartment(department.id)}
            startIcon={<Icon>archive</Icon>}
          >
            حذف
          </MDButton>
        </MDBox>
      ),
    }));
  };

  const fetchCompanies = async () => {
    try {
      const response = await RequestService.get(`Companies?skip=0&take=100`);
      if (response.data && !response.data.error) {
        setCompanies(response.data.data);
      } else {
        showNotification("حدث خطأ أثناء جلب الشركات", "error", response);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب الشركات", "error", error.response);
    }
  };

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      let response;
      const skip = (pagination.page - 1) * pagination.pageSize;
      const take = pagination.pageSize;

      if (selectedCompany === "all") {
        response = await RequestService.get(`Departments/get-departments?searchTerm=${search}&skip=${skip}&take=${take}`);
      } else {
        response = await RequestService.get(`Departments/by-company/${selectedCompany}?searchTerm=${search}&skip=${skip}&take=${take}`);
      }

      if (response.data && !response.data.error) {
        setTableData({
          ...tableData,
          rows: transformDepartmentData(response.data.data)
        });
        setPagination({
          ...pagination,
          totalItems: response.data.totalCount
        });
      } else {
        showNotification("حدث خطأ أثناء جلب الأقسام", "error", response);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
      showNotification(error.response?.data?.message || "حدث خطأ أثناء جلب الأقسام", "error", error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (event) => {
    setSelectedCompany(event.target.value);
    setPagination({ ...pagination, page: 1 }); // إعادة تعيين الصفحة إلى 1 عند تغيير الشركة
  };

  useEffect(() => {
    fetchDepartments();
    if (companies.length === 0) {
      fetchCompanies();
    }
  }, [selectedCompany, pagination.page, pagination.pageSize]);

  useEffect(() => {
    if (search || firstSearch) {
      setFirstSearch(true); {
        setPagination(prev => ({ ...prev, page: 1 }));
        const timer = setTimeout(() => {
          fetchDepartments();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [search, selectedCompany]);

  const renderDialog = (title, isOpen, onClose, onSubmit) => (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DepartmentForm
          formData={departmentForm}
          handleChange={setDepartmentForm}
          companies={companies}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', p: 2, gap: 1 }}>
        <MDButton onClick={onSubmit} variant="gradient" color="info" size="small">
          {title === "إضافة قسم جديد" ? "إضافة" : "حفظ التغييرات"}
        </MDButton>
        <MDButton onClick={onClose} variant="outlined" color="error" size="small">
          إلغاء
        </MDButton>
      </DialogActions>
    </Dialog>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={3}
                sx={{
                  backgroundColor: "grey.100",
                  borderBottom: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium">
                    قائمة الأقسام
                  </MDTypography>
                  <MDTypography variant="button" color="text">
                    إدارة الأقسام وبياناتها
                  </MDTypography>
                </MDBox>
                <MDBox width="14rem" ml="auto">
                  <MDInput
                    placeholder="بحث ..."
                    value={search}
                    fullWidth
                    onChange={({ currentTarget }) => {
                      setSearch(currentTarget.value);
                    }}
                    sx={{ margin: "0 -10px" }}
                  />
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={2}>
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>الشركة</InputLabel>
                    <Select
                      value={selectedCompany}
                      onChange={handleCompanyChange}
                      label="الشركة"
                      style={{ padding: 10 }}
                    >
                      <MenuItem value="all">جميع الشركات</MenuItem>
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MDButton
                    variant="gradient"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                  >
                    إضافة قسم
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: pagination.pageSize,
                    entries: [5, 10, 15, 20, 25],
                    onChange: handlePageSizeChange
                  }}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: pagination.page,
                    totalPages: Math.ceil(pagination.totalItems / pagination.pageSize),
                    totalItems: pagination.totalItems,
                    onPageChange: handlePageChange
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Department Dialog */}
      {renderDialog("إضافة قسم جديد", addDialog, handleCloseAddDialog, handleAddSubmit)}

      {/* Update Department Dialog */}
      {renderDialog("تحديث بيانات القسم", updateDialog.open, handleCloseUpdateDialog, handleUpdateSubmit)}

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
        dir="rtl"
      />

      <Footer />
    </DashboardLayout>
  );
}

export default DepartmentsDataTable;